import { useMemo } from 'react';
import AggregateActivitiesCard from '@/organisms/cards/AggregateActivities/AggregateActivitiesCard';
import EmptyDashboardCard from '@/organisms/cards/EmptyDashboard/EmptyDashboardCard';
import EntityMetricCards from '@/organisms/cards/EntityMetrics/EntityMetricCards';
import EntityScoreCard from '@/organisms/cards/EntityScore/EntityScoreCard';
import Matrix from '@/organisms/cards/QuadrantActivities/Matrix';
import MatrixType from '@/organisms/cards/QuadrantActivities/MatrixType';
import QuadrantActivitiesCard from '@/organisms/cards/QuadrantActivities/QuadrantActivitiesCard';
import QuickLinkCards from '@/organisms/cards/QuickLinks/QuickLinkCards';
import ScenarioCard from '@/organisms/cards/Scenario/ScenarioCard';
import ScenarioListCard from '@/organisms/cards/ScenarioList/ScenarioListCard';
import TagListCard from '@/organisms/cards/TagList/TagListCard';
import TeamListCard from '@/organisms/cards/TeamList/TeamListCard';
import TeamMemberListCard from '@/organisms/cards/TeamMembers/TeamMemberListCard';
import isEmptyState from '@/organisms/dashboards/utils/isEmptyState';
import { useDesignsContext } from '@/shared/providers/DesignsProvider';
import { useScenarioContext } from '@/shared/providers/ScenarioProvider';
import isTeamLead from '@/shared/utils/isTeamLead';
import env from '@beam-australia/react-env';
import Grid from '@mui/material/GridLegacy';
import useAccess from '@pkg/access/organisations/useAccess';
import useAuthStore from '@pkg/auth/useStore';
import { useUserContext } from '@/lib/accounts/users/UserProvider';
import { DesignEntity, EntityMetric, SurveyStatus } from '@/lib/enums';
import { BeamibleInsight } from '@/lib/enums';
import isSupporter from '@/components/DashboardContainer/utils/isSupporter';
import SurveysListCard from '../cards/SurveysList/SurveysListCard';

const Dashboard = () => {
  const { me } = useAuthStore();
  const { surveys } = useUserContext();
  const { scenario } = useScenarioContext();
  const designs = useDesignsContext();

  const entityType = scenario?.entity?.is_manager
    ? DesignEntity.MANAGER
    : scenario?.entity?.__type;
  const teamLead = isTeamLead(scenario?.entity, me.person?.uuid);
  const supporter = isSupporter(scenario?.entity, designs?.supporterMap, me.id);
  const access = useAccess();
  const canManage = access.model.manage;
  const showScenarios =
    !scenario?.details?.isScenario &&
    scenario?.entity?.__type === DesignEntity.ROLE;

  const entityId = scenario?.entity?.uuid ?? scenario?.entity?.__uuid;
  const scenarioSurveys = useMemo(() => {
    const OpenStatuses = new Set([
      SurveyStatus.NOT_STARTED,
      SurveyStatus.IN_PROGRESS,
    ]);

    const out = [];
    Object.values(surveys).forEach((survey) => {
      const roleId = survey.role?.id;
      if (!roleId || roleId !== entityId) return;
      if (!OpenStatuses.has(survey.status)) return;
      out.push(survey);
    });

    return out;
  }, [surveys, entityId?.toString()]);

  const isOrganisationView =
    scenario?.entity?.__type === DesignEntity.ORGANISATION;
  const isProd = env('PLATFORM_ENV') === 'production';

  const showScores =
    !isProd && (teamLead || supporter || (isOrganisationView && canManage));

  const showAlignment =
    entityType !== DesignEntity?.ROLE && (teamLead || supporter || canManage);

  const redirectToComparison =
    scenario?.entity?.is_manager || entityType === DesignEntity.ROLE;

  const showEmptyDashboard =
    isEmptyState({
      designs: designs?.designs,
      user: me,
      supporterMap: designs?.supporterMap,
    }) && !canManage;

  return (
    Boolean(scenario) && (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <ScenarioCard key={entityId} allowLink showNestedSwitch />
        </Grid>
        {scenarioSurveys.length > 0 && (
          <Grid item xs={12}>
            <SurveysListCard key={entityId} surveys={scenarioSurveys} />
          </Grid>
        )}
        {isOrganisationView && canManage && (
          <Grid item xs={12}>
            <EntityMetricCards
              metricList={[
                EntityMetric.GROUPS,
                EntityMetric.ROLES,
                EntityMetric.FTE,
                EntityMetric.BUDGET,
              ]}
            />
          </Grid>
        )}
        {showEmptyDashboard ? (
          <Grid item xs={12}>
            <EmptyDashboardCard />
          </Grid>
        ) : (
          <>
            {showScores && (
              <>
                <Grid item xs={6}>
                  <EntityScoreCard
                    key={`${entityId}:${scenario.details?.designId}:${BeamibleInsight.PRODUCTIVITY}`}
                    scoreType={BeamibleInsight.PRODUCTIVITY}
                  />
                </Grid>
                <Grid item xs={6}>
                  <EntityScoreCard
                    key={`${entityId}:${scenario.details?.designId}:${BeamibleInsight.WORK_HEALTH}`}
                    scoreType={BeamibleInsight.WORK_HEALTH}
                  />
                </Grid>
              </>
            )}
            {!isOrganisationView && (
              <Grid item xs={12}>
                <AggregateActivitiesCard
                  key={`${entityId}:${scenario.details?.designId}`}
                  entityType={entityType}
                />
              </Grid>
            )}
            {(!isOrganisationView || (isOrganisationView && canManage)) && (
              <>
                <Grid item xs={12}>
                  <QuadrantActivitiesCard
                    entityType={entityType}
                    id={MatrixType.PRIORITY}
                    key={`${entityId}:${scenario.details?.designId}:${MatrixType.PRIORITY}`}
                    matrix={Matrix[MatrixType.PRIORITY]}
                    title="Prioritisation Matrix"
                  />
                </Grid>
                {showAlignment && (
                  <Grid item xs={12}>
                    <QuadrantActivitiesCard
                      entityType={entityType}
                      id={MatrixType.ALIGNMENT}
                      key={`${entityId}:${scenario.details?.designId}:${MatrixType.ALIGNMENT}`}
                      matrix={Matrix[MatrixType.ALIGNMENT]}
                      title="Alignment on Priorities"
                    />
                  </Grid>
                )}
              </>
            )}
            {(!isOrganisationView || (isOrganisationView && canManage)) && (
              <Grid item xs={12}>
                <TagListCard
                  key={`${entityId}:${scenario.details?.designId}`}
                  title="Activity tags"
                />
              </Grid>
            )}
            {showScenarios && (
              <Grid item xs={12}>
                <ScenarioListCard
                  key={`${entityId}:${scenario.details?.designId}`}
                  entityType={scenario.entity?.__type}
                  redirectToComparison={redirectToComparison}
                />
              </Grid>
            )}
            {isOrganisationView && canManage && (
              <Grid item xs={12}>
                <QuickLinkCards />
              </Grid>
            )}
            {scenario?.details?.scope !== DesignEntity.ROLE && (
              <Grid item xs={12}>
                {entityType === DesignEntity.ORGANISATION ? (
                  <TeamListCard
                    allowLink
                    key={`${entityId}:${scenario.details?.designId}`}
                    title="Teams in your organisation"
                  />
                ) : (
                  <TeamMemberListCard
                    allowLink
                    key={`${entityId}:${scenario.details?.designId}`}
                    title="Members of your team"
                  />
                )}
              </Grid>
            )}
          </>
        )}
      </Grid>
    )
  );
};

export default Dashboard;
